import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, Mail } from "lucide-react";
import { NAV, COMPANY } from "@/data/content";
import ProfileMenu from "./ProfileMenu";

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/95 backdrop-blur-md shadow-[0_1px_0_0_#E2E8F0,0_4px_16px_-8px_rgba(11,35,65,0.08)]"
          : "bg-white"
      }`}
    >
      {/* Top utility bar — slim & refined */}
      <div className="hidden md:block bg-[#0B2341] text-white/70 text-[11px]">
        <div className="container-corp flex justify-between items-center h-9">
          <div className="flex items-center gap-6 tracking-wide">
            <span>
              <span className="text-white/45 mr-1.5">CIN</span>
              <span className="font-mono">{COMPANY.cin}</span>
            </span>
            <span className="text-white/25">|</span>
            <span className="text-[#16A34A] uppercase tracking-[0.2em] font-bold">
              ISO 9001:2015
            </span>
          </div>
          <div className="flex items-center gap-5">
            <a
              href={`tel:${COMPANY.phone}`}
              className="inline-flex items-center gap-1.5 hover:text-[#16A34A] transition-colors"
              data-testid="header-phone-link"
            >
              <Phone size={11} strokeWidth={2} /> {COMPANY.phone}
            </a>
            <span className="text-white/25">|</span>
            <a
              href={`mailto:${COMPANY.email}`}
              className="inline-flex items-center gap-1.5 hover:text-[#16A34A] transition-colors"
              data-testid="header-email-link"
            >
              <Mail size={11} strokeWidth={2} /> {COMPANY.email}
            </a>
          </div>
        </div>
      </div>

      {/* Main nav */}
      <div className="container-corp flex items-center justify-between h-[72px] lg:h-[80px] gap-6">
        {/* Logo */}
        <Link
          to="/"
          data-testid="logo-link"
          className="flex items-center gap-3 group shrink-0"
        >
          <img
            src="/vpcl-mark.png"
            alt=""
            aria-hidden="true"
            className="h-10 w-10 object-contain"
          />
          <div className="leading-none">
            <div
              className="font-heading font-bold text-[#0B2341] text-[17px] tracking-tight"
              data-testid="header-logo"
            >
              VPCL <span className="text-[#16A34A]">INFRA</span>
            </div>
            <div className="mt-1 text-[9px] uppercase tracking-[0.28em] text-[#4A5568] font-semibold">
              Renewable Infrastructure
            </div>
          </div>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-1 flex-1 justify-center">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-link-${item.label.toLowerCase()}`}
              className={({ isActive }) =>
                `relative text-[13px] font-medium tracking-wide px-3 py-2 transition-colors group ${
                  isActive ? "text-[#0B2341]" : "text-[#4A5568] hover:text-[#0B2341]"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {item.label}
                  <span
                    className={`absolute left-3 right-3 -bottom-0.5 h-[2px] bg-[#16A34A] origin-left transition-transform duration-300 ${
                      isActive ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"
                    }`}
                  />
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Right cluster */}
        <div className="hidden lg:flex items-center gap-3 shrink-0">
          <ProfileMenu />
          <span className="w-px h-6 bg-[#E2E8F0]" aria-hidden="true" />
          <Link
            to="/contact"
            className="inline-flex items-center justify-center bg-[#16A34A] hover:bg-[#0E7A36] text-white font-medium tracking-wide text-[12px] px-5 py-2.5 rounded-none transition-colors duration-200"
            data-testid="header-cta"
          >
            Discuss Project
          </Link>
        </div>

        {/* Mobile toggle */}
        <button
          onClick={() => setOpen(!open)}
          className="lg:hidden p-2 text-[#0B2341] -mr-2"
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div
          className="lg:hidden border-t border-[#E2E8F0] bg-white shadow-lg"
          data-testid="mobile-menu"
        >
          <div className="container-corp py-5 flex flex-col">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `text-[15px] font-medium py-3.5 border-b border-[#E2E8F0] flex items-center justify-between ${
                    isActive ? "text-[#0B2341]" : "text-[#4A5568]"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <span>{item.label}</span>
                    {isActive && <span className="w-1.5 h-1.5 rounded-full bg-[#16A34A]" />}
                  </>
                )}
              </NavLink>
            ))}
            <Link
              to="/contact"
              className="mt-5 inline-flex items-center justify-center bg-[#16A34A] hover:bg-[#0E7A36] text-white font-medium tracking-wide text-sm px-6 py-3 transition-colors"
              data-testid="mobile-cta"
            >
              Discuss Your Project
            </Link>
            <ProfileMenu variant="mobile" />
          </div>
        </div>
      )}
    </header>
  );
}
